/**
 * projectionUtils — pure geometry functions for cross-view segmentation projection.
 *
 * Computes 3D bounding boxes from contour polylines and projects them onto
 * target viewports using Cornerstone's worldToCanvas() for robust coordinate
 * mapping (handles arbitrary DICOM orientations and direction cosines).
 */

/** Default slice tolerance fallback in mm when image spacing is unavailable. */
const DEFAULT_SLICE_TOLERANCE_MM = 2.0;

/** Compute half-slice-spacing tolerance from a viewport's image data. */
function getSliceTolerance(viewport: any): number {
  try {
    const imageData = viewport.getImageData?.();
    if (imageData?.spacing) {
      return (imageData.spacing[2] ?? imageData.spacing[0]) / 2;
    }
  } catch (e) {
    console.warn("[projectionUtils] getSliceTolerance failed, using fallback:", e);
  }
  return DEFAULT_SLICE_TOLERANCE_MM;
}

export type PlaneType = "Axial" | "Coronal" | "Sagittal";

export interface BBox3D {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
}

/** Normalized rectangle in viewport space (0-1 for both axes). */
export interface NormalizedRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Compute the axis-aligned 3D bounding box of a contour polyline.
 *
 * @param polyline - Array of [x, y, z] world-coordinate points, or a flat
 *   Float32Array/number[] with stride 3.
 */
export function computeContourBBox(polyline: number[][] | ArrayLike<number>): BBox3D | null {
  // Handle both nested array and flat array formats
  const isFlat = !Array.isArray((polyline as any)[0]);
  const len = isFlat ? (polyline as ArrayLike<number>).length / 3 : (polyline as number[][]).length;
  if (len < 1) return null;

  let minX = Infinity,
    maxX = -Infinity;
  let minY = Infinity,
    maxY = -Infinity;
  let minZ = Infinity,
    maxZ = -Infinity;

  if (isFlat) {
    const flat = polyline as ArrayLike<number>;
    for (let i = 0; i < flat.length; i += 3) {
      const x = flat[i],
        y = flat[i + 1],
        z = flat[i + 2];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
  } else {
    for (const pt of polyline as number[][]) {
      const [x, y, z] = pt;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
  }

  return { minX, maxX, minY, maxY, minZ, maxZ };
}

/**
 * Merge multiple bounding boxes into one encompassing bbox.
 */
export function mergeBBoxes(bboxes: BBox3D[]): BBox3D | null {
  if (bboxes.length === 0) return null;
  const result: BBox3D = { ...bboxes[0] };
  for (let i = 1; i < bboxes.length; i++) {
    const b = bboxes[i];
    if (b.minX < result.minX) result.minX = b.minX;
    if (b.maxX > result.maxX) result.maxX = b.maxX;
    if (b.minY < result.minY) result.minY = b.minY;
    if (b.maxY > result.maxY) result.maxY = b.maxY;
    if (b.minZ < result.minZ) result.minZ = b.minZ;
    if (b.maxZ > result.maxZ) result.maxZ = b.maxZ;
  }
  return result;
}

/** Get the 8 corners of a 3D bounding box. */
function getBBoxCorners(b: BBox3D): [number, number, number][] {
  return [
    [b.minX, b.minY, b.minZ],
    [b.maxX, b.minY, b.minZ],
    [b.minX, b.maxY, b.minZ],
    [b.maxX, b.maxY, b.minZ],
    [b.minX, b.minY, b.maxZ],
    [b.maxX, b.minY, b.maxZ],
    [b.minX, b.maxY, b.maxZ],
    [b.maxX, b.maxY, b.maxZ],
  ];
}

/**
 * Project a 3D bounding box onto a Cornerstone viewport using worldToCanvas().
 *
 * This approach is orientation-agnostic — it delegates all coordinate mapping
 * to Cornerstone, which correctly handles DICOM direction cosines, image
 * flips, and non-standard orientations.
 *
 * The bbox is considered to intersect the viewport's current slice if the
 * camera's focal point (along its view-plane normal) falls within the bbox's
 * extent along that same normal.
 *
 * @param bbox - 3D bounding box of the annotation in world coordinates
 * @param viewport - Cornerstone viewport with worldToCanvas() and getCamera()
 * @returns Normalized rectangle (0-1), or null if the bbox does not intersect
 */
/**
 * Check whether a bbox intersects the viewport's current slice along the
 * camera's view-plane normal. Returns false if the annotation is too far
 * from the current slice to be relevant.
 */
export function bboxIntersectsViewport(bbox: BBox3D, viewport: any): boolean {
  if (!viewport?.getCamera) return false;
  const camera = viewport.getCamera();
  if (!camera?.viewPlaneNormal || !camera?.focalPoint) return false;

  const vpn = camera.viewPlaneNormal as [number, number, number];
  const fp = camera.focalPoint as [number, number, number];
  const focalDepth = fp[0] * vpn[0] + fp[1] * vpn[1] + fp[2] * vpn[2];

  const corners = getBBoxCorners(bbox);
  let minDepth = Infinity,
    maxDepth = -Infinity;
  for (const c of corners) {
    const depth = c[0] * vpn[0] + c[1] * vpn[1] + c[2] * vpn[2];
    if (depth < minDepth) minDepth = depth;
    if (depth > maxDepth) maxDepth = depth;
  }

  const sliceTolerance = getSliceTolerance(viewport);

  return !(focalDepth + sliceTolerance < minDepth || focalDepth - sliceTolerance > maxDepth);
}

/**
 * Project all contour annotations onto a target viewport and compute the
 * silhouette outline as an SVG path.
 *
 * For each contour (on each source slice), every polyline point is projected
 * through viewport.worldToCanvas(). The projected points are binned by
 * canvas-Y, and the left/right extents at each Y level define the silhouette
 * polygon.
 *
 * @param viewport - Cornerstone viewport (target)
 * @param annotations - All contour annotations from Cornerstone state
 * @param activeSegmentIndex - Which segment index to project
 * @returns SVG path string and CSS-pixel bounds, or null if nothing to project
 */
export function computeProjectedSilhouette(
  viewport: any,
  annotations: any[],
  activeSegmentIndex: number,
): { svgPath: string; cssWidth: number; cssHeight: number } | null {
  if (!viewport?.worldToCanvas || !viewport?.element) return null;

  const element = viewport.element;
  const cssWidth = element.clientWidth;
  const cssHeight = element.clientHeight;
  if (cssWidth <= 0 || cssHeight <= 0) return null;

  // Get the target viewport's view plane normal to skip same-plane annotations
  // (projecting an axial contour onto another axial viewport produces a degenerate line)
  let vpNormal: number[] | null = null;
  try {
    const camera = viewport.getCamera?.();
    vpNormal = camera?.viewPlaneNormal ?? null;
  } catch (e) {
    console.warn("[projectionUtils] getCamera failed:", e);
  }

  // Collect projected canvas points from all matching contours.
  // Also track per-annotation depth along the view-plane normal so we can
  // detect inter-slice spacing and pad the silhouette by half a slice thickness.
  const yBins = new Map<number, { minX: number; maxX: number }>();
  let totalPoints = 0;
  const sliceDepths = new Set<number>(); // depth per source slice (rounded to 0.1mm)
  let firstPt: number[] | null = null; // for canvas-pixel-per-mm calibration

  for (const ann of annotations) {
    if (ann.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") continue;
    if (ann.data?.segmentation?.segmentIndex !== activeSegmentIndex) continue;
    if (ann.autoGenerated) continue;

    // Skip annotations on the same plane — cross-view projection only makes sense
    // between orthogonal planes (e.g. axial contour projected onto coronal/sagittal)
    const annNormal = ann.metadata?.viewPlaneNormal;
    if (vpNormal && annNormal) {
      const dot = Math.abs(
        vpNormal[0] * annNormal[0] + vpNormal[1] * annNormal[1] + vpNormal[2] * annNormal[2],
      );
      if (dot > 0.9) continue; // parallel planes — skip
    }

    const polyline = ann.data?.contour?.polyline;
    if (!polyline || polyline.length < 3) continue;

    // Record the depth of this slice (first point is representative)
    if (vpNormal) {
      const p0 = polyline[0];
      const depth = p0[0] * vpNormal[0] + p0[1] * vpNormal[1] + p0[2] * vpNormal[2];
      sliceDepths.add(Math.round(depth * 10) / 10);
    }

    for (const pt of polyline) {
      if (!firstPt) firstPt = pt;
      let canvasPt: [number, number] | null = null;
      try {
        canvasPt = viewport.worldToCanvas(pt);
      } catch (e) {
        console.warn("[projectionUtils] worldToCanvas failed for point:", e);
        continue;
      }
      if (!canvasPt) continue;
      const [cx, cy] = canvasPt;
      if (!isFinite(cx) || !isFinite(cy)) continue;
      totalPoints++;
      // Bin by rounded canvas-Y (each source slice maps to a distinct Y)
      const binY = Math.round(cy);
      const existing = yBins.get(binY);
      if (existing) {
        if (cx < existing.minX) existing.minX = cx;
        if (cx > existing.maxX) existing.maxX = cx;
      } else {
        yBins.set(binY, { minX: cx, maxX: cx });
      }
    }
  }

  if (yBins.size === 0 || totalPoints === 0) return null;

  // Build silhouette polygon: left edge (top→bottom), right edge (bottom→top)
  const sortedYs = Array.from(yBins.keys()).sort((a, b) => a - b);

  // Detect source slice thickness from inter-slice spacing along view-plane normal,
  // then pad the silhouette by ±half that thickness so each slice appears as a band
  // rather than a 1px line on the orthogonal viewport.
  let halfPadPx = 2; // fallback for single-slice case
  if (sliceDepths.size >= 2 && vpNormal && firstPt) {
    const depthArr = Array.from(sliceDepths).sort((a, b) => a - b);
    let minGap = Infinity;
    for (let i = 1; i < depthArr.length; i++) {
      const gap = depthArr[i] - depthArr[i - 1];
      if (gap > 0.5) minGap = Math.min(minGap, gap);
    }
    if (isFinite(minGap)) {
      // Convert half-thickness (world mm) to canvas pixels via worldToCanvas
      try {
        const refCanvas = viewport.worldToCanvas(firstPt);
        // Offset the point along the view-plane normal by the slice thickness
        const offsetPt = [
          firstPt[0] + vpNormal[0] * minGap,
          firstPt[1] + vpNormal[1] * minGap,
          firstPt[2] + vpNormal[2] * minGap,
        ];
        const offsetCanvas = viewport.worldToCanvas(offsetPt);
        if (refCanvas && offsetCanvas) {
          const dyPx = Math.abs(offsetCanvas[1] - refCanvas[1]);
          const dxPx = Math.abs(offsetCanvas[0] - refCanvas[0]);
          // Use the larger canvas delta (accounts for oblique normals)
          const deltaPx = Math.max(dyPx, dxPx);
          if (deltaPx > 1) halfPadPx = Math.round(deltaPx / 2);
        }
      } catch (e) {
        console.warn(
          "[projectionUtils] slice thickness calibration failed, using default padding:",
          e,
        );
      }
    }
  }

  // Extend the Y range by ±halfPadPx to account for slice thickness
  {
    const topY = sortedYs[0] - halfPadPx;
    const botY = sortedYs[sortedYs.length - 1] + halfPadPx;
    const topBin = yBins.get(sortedYs[0])!;
    const botBin = yBins.get(sortedYs[sortedYs.length - 1])!;
    yBins.set(topY, { ...topBin });
    yBins.set(botY, { ...botBin });
    sortedYs.unshift(topY);
    sortedYs.push(botY);
  }

  const leftEdge: string[] = [];
  const rightEdge: string[] = [];

  for (const y of sortedYs) {
    const bin = yBins.get(y)!;
    leftEdge.push(`${bin.minX} ${y}`);
    rightEdge.push(`${bin.maxX} ${y}`);
  }

  rightEdge.reverse();

  const svgPath =
    `M ${leftEdge[0]} ` +
    leftEdge
      .slice(1)
      .map((p) => `L ${p}`)
      .join(" ") +
    " " +
    rightEdge.map((p) => `L ${p}`).join(" ") +
    " Z";

  return { svgPath, cssWidth, cssHeight };
}

export function projectBBoxViaViewport(bbox: BBox3D, viewport: any): NormalizedRect | null {
  if (!viewport?.worldToCanvas || !viewport?.getCamera || !viewport?.element) return null;

  const camera = viewport.getCamera();
  if (!camera?.viewPlaneNormal || !camera?.focalPoint) return null;

  const { viewPlaneNormal, focalPoint } = camera;
  const vpn = viewPlaneNormal as [number, number, number];
  const fp = focalPoint as [number, number, number];

  // Compute the depth of the focal point along the view-plane normal
  const focalDepth = fp[0] * vpn[0] + fp[1] * vpn[1] + fp[2] * vpn[2];

  // Compute the depth range of the bbox along the view-plane normal
  const corners = getBBoxCorners(bbox);
  let minDepth = Infinity,
    maxDepth = -Infinity;
  for (const c of corners) {
    const depth = c[0] * vpn[0] + c[1] * vpn[1] + c[2] * vpn[2];
    if (depth < minDepth) minDepth = depth;
    if (depth > maxDepth) maxDepth = depth;
  }

  const sliceTolerance = getSliceTolerance(viewport);

  // Check if the bbox intersects the current slice
  if (focalDepth + sliceTolerance < minDepth || focalDepth - sliceTolerance > maxDepth) {
    return null;
  }

  // Project all 8 corners to canvas space
  let canvasMinX = Infinity,
    canvasMaxX = -Infinity;
  let canvasMinY = Infinity,
    canvasMaxY = -Infinity;

  for (const corner of corners) {
    const canvasPt = viewport.worldToCanvas(corner);
    if (!canvasPt) continue;
    const [cx, cy] = canvasPt;
    if (cx < canvasMinX) canvasMinX = cx;
    if (cx > canvasMaxX) canvasMaxX = cx;
    if (cy < canvasMinY) canvasMinY = cy;
    if (cy > canvasMaxY) canvasMaxY = cy;
  }

  if (!isFinite(canvasMinX) || !isFinite(canvasMinY)) return null;

  // Get CSS dimensions for normalization.
  // worldToCanvas() returns CSS pixel coordinates, so we must normalize by
  // the element's CSS size (clientWidth/clientHeight), NOT the canvas pixel
  // buffer (canvas.width/height) which is scaled by devicePixelRatio.
  const element = viewport.element;
  if (!element) return null;
  const canvasWidth = element.clientWidth;
  const canvasHeight = element.clientHeight;
  if (canvasWidth <= 0 || canvasHeight <= 0) return null;

  // Normalize to 0-1
  const left = canvasMinX / canvasWidth;
  const top = canvasMinY / canvasHeight;
  const width = (canvasMaxX - canvasMinX) / canvasWidth;
  const height = (canvasMaxY - canvasMinY) / canvasHeight;

  // Clamp to viewport
  const clampedLeft = Math.max(0, Math.min(1, left));
  const clampedTop = Math.max(0, Math.min(1, top));
  const clampedRight = Math.max(0, Math.min(1, left + width));
  const clampedBottom = Math.max(0, Math.min(1, top + height));

  const clampedWidth = clampedRight - clampedLeft;
  const clampedHeight = clampedBottom - clampedTop;

  if (clampedWidth <= 0 || clampedHeight <= 0) return null;

  return {
    left: clampedLeft,
    top: clampedTop,
    width: clampedWidth,
    height: clampedHeight,
  };
}
